import { useState, useCallback } from 'react';
import { Node, Edge } from '@xyflow/react';
import { NodeData } from '../initial-nodes';

export function useDiagramMultiSelection() {
  const [selectedElements, setSelectedElements] = useState<{
    nodes: Node<NodeData>[];
    edges: Edge[];
  }>({
    nodes: [],
    edges: []
  });

  // Adicionar/remover node da seleção
  const toggleNodeSelection = useCallback((node: Node<NodeData>, multiSelect: boolean) => {
    setSelectedElements(prev => {
      if (multiSelect) {
        // Multi-seleção: adicionar ou remover da lista
        const isAlreadySelected = prev.nodes.some(n => n.id === node.id);
        if (isAlreadySelected) {
          return {
            ...prev,
            nodes: prev.nodes.filter(n => n.id !== node.id)
          };
        } else {
          return {
            ...prev,
            nodes: [...prev.nodes, node]
          };
        }
      } else {
        // Seleção única: substituir toda a seleção
        return {
          nodes: [node],
          edges: []
        };
      }
    });
  }, []);

  // Adicionar/remover edge da seleção
  const toggleEdgeSelection = useCallback((edge: Edge, multiSelect: boolean) => {
    setSelectedElements(prev => {
      if (multiSelect) {
        // Multi-seleção: adicionar ou remover da lista
        const isAlreadySelected = prev.edges.some(e => e.id === edge.id);
        if (isAlreadySelected) {
          return {
            ...prev,
            edges: prev.edges.filter(e => e.id !== edge.id)
          };
        } else {
          return {
            ...prev,
            edges: [...prev.edges, edge]
          };
        }
      } else {
        // Seleção única: substituir toda a seleção
        return {
          nodes: [],
          edges: [edge]
        };
      }
    });
  }, []);

  // Selecionar múltiplos elementos (usado pelo drag box)
  const setMultipleSelection = useCallback((nodes: Node<NodeData>[], edges: Edge[]) => {
    setSelectedElements({ nodes, edges });
  }, []);

  // Limpar toda a seleção
  const clearSelection = useCallback(() => {
    setSelectedElements({
      nodes: [],
      edges: []
    });
  }, []);

  // Verificar se há múltiplos elementos selecionados
  const hasMultipleSelection = selectedElements.nodes.length + selectedElements.edges.length > 1;

  // Verificar se há seleção única
  const hasSingleSelection = selectedElements.nodes.length + selectedElements.edges.length === 1;

  // Obter elemento único selecionado (se houver)
  const getSingleSelected = useCallback(() => {
    if (selectedElements.nodes.length === 1 && selectedElements.edges.length === 0) {
      return { type: 'node' as const, element: selectedElements.nodes[0] };
    }
    if (selectedElements.edges.length === 1 && selectedElements.nodes.length === 0) {
      return { type: 'edge' as const, element: selectedElements.edges[0] };
    }
    return null;
  }, [selectedElements]);

  return {
    selectedElements,
    toggleNodeSelection,
    toggleEdgeSelection,
    setMultipleSelection,
    clearSelection,
    hasMultipleSelection,
    hasSingleSelection,
    getSingleSelected
  };
}